import { PasswordGenerator } from './../shared/password-generator';
import { DomainUserUtil } from './../shared/utils/domain-user-util';
import { LanguageService } from './../services/language.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Role, UserAccountClient } from './../models/models.swagger.generated';
import { DropDownConstantsFactory } from './../shared/syncfusion/dropdown-constants-factory';
import { Component, Input, OnInit, OnDestroy, AfterViewChecked, ChangeDetectorRef, ViewChild } from '@angular/core';
import { AbstractControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ClientDTO } from '../models/models.swagger.generated';
import { AuthService } from '../services/auth.service';
import { Subscription } from 'rxjs';
import { ClientService } from '../services/client/client.service';
import { startWith } from 'rxjs/operators';
import { PasswordVisibilityDirective } from '../../directives/password-visibility.directive';
import { FormHelper } from '../shared/form-helper';
import { UserEditFormType } from './user-edit-form-type';
import { AppErrorMessagesComponent } from '../error-messages/app-error-messages.component';
import { ClientAutocompleteComponent } from '../shared/client-autocomplete/client-autocomplete.component';
import { NgClass } from '@angular/common';
import { CheckBoxModule } from '@syncfusion/ej2-angular-buttons';
import { DropDownListAllModule } from '@syncfusion/ej2-angular-dropdowns';
import { YesNoGroupButtonComponent } from '../shared/yes-no-group-button/yes-no-group-button.component';
import { passwordValidation } from 'src/app/directives/password-validation.directive';
import { CustomValidatorsFactory } from '../shared/validators/custom-validators-factory';

@Component({
    selector: 'app-user-edit-form',
    templateUrl: './user-edit-form.component.html',
    styleUrls: ['./user-edit-form.component.scss'],
    imports: [NgClass, TranslateModule, AppErrorMessagesComponent, ClientAutocompleteComponent, ReactiveFormsModule, YesNoGroupButtonComponent, CheckBoxModule, DropDownListAllModule, PasswordVisibilityDirective]
})
export class UserEditFormComponent implements OnInit, AfterViewChecked, OnDestroy {
  @Input() public userEditForm: FormGroup<UserEditFormType>;
  @Input() public selectedClient: ClientDTO = null;

  @ViewChild(PasswordVisibilityDirective)
  public passwordVisibilityDirective: PasswordVisibilityDirective

  public dropdownSettings = DropDownConstantsFactory.createStandardClientDropdownSettings();
  public languageDropdownSettings = { text: 'value', value: 'key' };
  public Role = Role;
  public isAdd: boolean = true;
  public languageDropDownItems: any[] = [];
  private clientRoles = [Role.InterfaceServiceAccess];

  public isDomainUser: boolean;

  public get formFields() {
    return this.userEditForm.controls;
  }

  public get clients(): ClientDTO[] {
    return this.clientService.clients;
  }

  public get client(): ClientDTO {
    return this.userEditForm.controls.client?.value as ClientDTO
  }

  public get isLoggedInUser(): boolean {
    return this.userEditForm.getRawValue().userId === this.authService.getUserInfo().userId;
  }

  public get isLoggedInUserAdmin(): boolean {
    return this.authService.getUserInfo().roles.includes(Role.Admin);
  }


  public get isClientRequired(): boolean {
    return this.userEditForm.controls.roles.value.includes(Role.InterfaceServiceAccess)
        || (this.userEditForm.getRawValue().roles != null && this.userEditForm.getRawValue().roles.some(r => this.clientRoles.indexOf(r) > -1));
  }

  private subscriptions = new Subscription();

  constructor(
    private authService: AuthService,
    private clientService: ClientService,
    private languageService: LanguageService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private userAccountClient: UserAccountClient,
    private translateService: TranslateService) {
  }

  public ngOnInit() {
    this.isAdd = !this.userEditForm.getRawValue().userId;
    this.isDomainUser = DomainUserUtil.isDomainUser(this.userEditForm.controls.userName.value);
    this.enableFormControls();

    //Language dropdown translation
    this.subscriptions.add(this.translate.onLangChange.pipe(startWith({})).subscribe(v => {
      this.languageDropDownItems = this.languageService.getLanguages()
        .map(l => ({ key: l, value: this.translate.instant('i18n.base.shared.Language.' + l) }));
    }));

    //Get Service Access Password
    if (this.formFields.roles.value.includes(Role.InterfaceServiceAccess) && this.formFields.userId.value) {
      this.userAccountClient.getServiceUserPassword(this.formFields.userId.value).subscribe((password: string) => {
        if (password) {
          this.formFields.password.setValue(password);
        }
      });
    }
  }

  public ngAfterViewChecked(): void {
    this.cdr.detectChanges();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public isUserInRole(role: Role): boolean {
    return this.userEditForm.controls.roles?.value?.includes(role)
  }

  public doesUserNeedPasswordField(): boolean {
    return this.userEditForm.controls.roles?.value?.includes(Role.AssetStorageGroupMember) || this.userEditForm.controls.roles?.value?.includes(Role.InterfaceServiceAccess);
  }

  public hasError(control: AbstractControl) {
    return FormHelper.hasErrorStatusToShow(control);
  }

  public hideClient() {
    return this.authService.getUserInfo().clientId || this.isLoggedInUser || this.formFields.roles.value.includes(Role.AssetStorageGroupMember);
  }

  public isClientSelected() {
    return !this.isAdd && this.userEditForm.controls.client.value
  }

  public generatePassword(): void {
    this.formFields.password.setValue(PasswordGenerator.generatePassword());
    this.passwordVisibilityDirective.setVisible();
  }

  public formatRoles(roles: Role[]): string {
    if (!roles) {
      return '';
    }
    return roles.map(r => this.translateService.instant('i18n.shared.roles.' + Role[r])).join(', ');
  }

  private enableFormControls(): void {
    var form = this.userEditForm;
    if (form.controls.roles.value.includes(Role.AssetStorageGroupMember)) {
      form.controls.email.enable();
      form.controls.language.enable();
      form.controls.password.disable();
      form.controls.phoneNumber.enable();
      form.controls.firstname.enable();
      form.controls.lastname.enable();
      form.controls.userName.disable();
      //form.controls.password.setValidators(null);
    }
    else if (form.controls.roles.value.includes(Role.InterfaceServiceAccess)) {
      form.controls.password.enable();
      form.controls.email.enable();
      if (!form.controls.userId.value) {
        form.controls.client.enable();
        form.controls.userName.enable();
      }
    } else {
      form.controls.language.enable();
      form.controls.phoneNumber.enable();
    }

    if (this.isDomainUser) {
      form.controls.enabled.disable();
    }

    if (this.isLoggedInUser) {
      form.controls.enabled.disable();
      form.controls.roles.disable();
      form.controls.userName.disable();
      form.controls.password.disable();
    }
  }
}
